
import { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import { getCategories } from "@/services/promptService";
import type { Category } from "@/services/promptService";

interface UseExploreInitializationProps {
  categoryParam: string | null;
}

export function useExploreInitialization({ categoryParam }: UseExploreInitializationProps) {
  const [categories, setCategories] = useState<Category[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [categoryIdFromSlug, setCategoryIdFromSlug] = useState<string | null>(null);

  // State flags for preventing race conditions
  const initialCategoriesLoaded = useRef(false);
  const initialLoadComplete = useRef(false);
  const isMounted = useRef(true);
  const categoriesLoadAttempted = useRef(false);

  // Set up mounted ref for cleanup
  useEffect(() => {
    isMounted.current = true;
    console.log("useExploreInitialization mounting");
    
    return () => {
      isMounted.current = false;
      console.log("useExploreInitialization unmounting");
    };
  }, []);

  // Load categories first
  useEffect(() => {
    const loadCategories = async () => {
      // Prevent multiple loading attempts
      if (initialCategoriesLoaded.current || categoriesLoadAttempted.current) {
        console.log("Categories already loaded or attempted, skipping");
        return;
      }
      
      categoriesLoadAttempted.current = true;
      
      try {
        console.log("Loading categories...");
        setIsLoading(true);
        const cats = await getCategories();
        
        if (!isMounted.current) return;
        
        // Always set categories even if empty
        setCategories(cats || []);
        console.log(`Categories loaded: ${cats?.length || 0}`);
        
        // Process category from URL if present
        if (categoryParam && cats && cats.length > 0) {
          console.log(`Looking for category with slug: ${categoryParam}`);
          const matchingCategory = cats.find(cat => cat.slug === categoryParam);
          if (matchingCategory) {
            console.log(`Found matching category: ${matchingCategory.name} with ID: ${matchingCategory.id}`);
            setCategoryIdFromSlug(matchingCategory.id);
          } else {
            console.log(`No category found with slug: ${categoryParam}`);
          }
        } else {
          console.log("No category param or no categories loaded");
          setCategoryIdFromSlug(null);
        }
        
        initialCategoriesLoaded.current = true;
        initialLoadComplete.current = true;
      } catch (error) {
        console.error("Error loading categories:", error);
        toast.error("Failed to load categories");
        // Even on error, mark as attempted to prevent infinite retries
        initialCategoriesLoaded.current = true;
      } finally {
        if (isMounted.current) {
          setIsLoading(false);
        }
      }
    };
    
    loadCategories();
  }, [categoryParam]);

  // Update category ID if categoryParam changes after initial load
  useEffect(() => {
    if (!initialCategoriesLoaded.current || categories.length === 0) {
      return;
    }

    if (categoryParam) {
      console.log(`URL category param changed to: ${categoryParam}, finding matching category`);
      const matchingCategory = categories.find(cat => cat.slug === categoryParam);
      if (matchingCategory) {
        console.log(`Found matching category for new param: ${matchingCategory.name} (${matchingCategory.id})`);
        setCategoryIdFromSlug(matchingCategory.id);
      } else {
        console.log(`No category found for new slug: ${categoryParam}`);
        setCategoryIdFromSlug(null);
      }
    } else {
      console.log("Category param cleared, resetting categoryIdFromSlug");
      setCategoryIdFromSlug(null);
    }
  }, [categoryParam, categories]);

  return {
    categories,
    isLoading,
    categoryIdFromSlug,
    initialCategoriesLoaded,
    initialLoadComplete
  };
}
